import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wallet, Coins, Leaf, MapPin, ShieldCheck, BadgeCheck, Star, Lock, Shield, Scale,
  Check, X, ArrowRight,
} from "lucide-react";

const WHY = [
  { icon: Wallet, title: "Save Money", body: "Rent instead of buying expensive equipment you'll rarely use." },
  { icon: Coins, title: "Earn Extra Income", body: "Turn underused tools and equipment into a source of income." },
  { icon: Leaf, title: "Support Sustainability", body: "Reduce waste by sharing and reusing existing resources." },
  { icon: MapPin, title: "Shop Local", body: "Find useful items from people in your own community." },
  { icon: ShieldCheck, title: "Secure Platform", body: "Built with trust, transparency, and secure transactions in mind." },
];

const TRUST = [
  { icon: BadgeCheck, title: "Verified Accounts", body: "Users are encouraged to verify their identity, email address, and mobile number." },
  { icon: Star, title: "Review System", body: "After every completed transaction, users can leave ratings and reviews." },
  { icon: Lock, title: "Secure Payments", body: "All payments and deposits are processed securely by Stripe — we never store or see your card details." },
  { icon: Shield, title: "Security Deposits", body: "Owners can choose to require a refundable security deposit for added protection." },
  { icon: Scale, title: "Dispute Resolution", body: "Where issues arise, SelLend provides a dispute process to help both parties reach a fair resolution." },
];

const DO = ["Treat others respectfully", "Provide accurate listing information", "Return rented items on time", "Maintain agreed item condition", "Communicate honestly and professionally"];
const DONT = ["Use false identities", "Misrepresent items", "Attempt transactions outside the platform", "Engage in fraudulent activity", "Harass or abuse other users"];

export default function About() {
  return (
    <div data-testid="about-page" className="min-h-screen">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-5">About SelLend</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
            Welcome to <span className="text-[#FF5A00]">SelLend</span>
          </h1>
          <p className="text-lg text-muted-foreground mt-6 leading-relaxed">
            SelLend is Ireland's marketplace for renting, buying, and selling everyday equipment and gear. We believe that not everything needs to be bought new. Many valuable items sit unused in garages, sheds, attics, and storage rooms most of the year. At the same time, other people need those same items for a day, a weekend, or a one-time project.
          </p>
          <p className="text-lg text-muted-foreground mt-4 leading-relaxed">
            SelLend helps connect people who own equipment with people who need it — whether it's a power drill for a DIY project, camping gear for a weekend trip, gardening equipment, sports gear, or household tools. Our mission is to help communities save money, earn extra income, reduce waste, and make better use of the equipment already available around us.
          </p>
        </motion.div>
      </section>

      {/* Why choose */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 border-t border-black/5">
        <h2 className="font-display text-3xl font-bold tracking-tight mb-10">Why choose SelLend?</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY.map((w, i) => (
            <motion.div
              key={w.title} data-testid={`about-why-${i}`}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white border border-black/10 rounded-2xl p-6 hover:border-[#FF5A00]/40 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-[#FFF0E8] flex items-center justify-center mb-4">
                <w.icon className="w-5 h-5 text-[#FF5A00]" />
              </div>
              <h3 className="font-semibold text-lg">{w.title}</h3>
              <p className="text-muted-foreground text-sm mt-1.5 leading-relaxed">{w.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust & safety */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-4">Trust &amp; Safety</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">Building trust within our community</h2>
          <p className="text-white/60 mt-4 max-w-2xl leading-relaxed">
            Trust is the foundation of every successful marketplace. SelLend includes several features designed to promote safe and responsible transactions.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {TRUST.map((t, i) => (
              <div key={t.title} data-testid={`about-trust-${i}`} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                <t.icon className="w-6 h-6 text-[#FF5A00] mb-4" />
                <h3 className="font-semibold text-lg">{t.title}</h3>
                <p className="text-white/60 text-sm mt-1.5 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community guidelines */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-display text-3xl font-bold tracking-tight">Community guidelines</h2>
        <p className="text-muted-foreground mt-3">To keep SelLend safe and enjoyable, all users must follow a few simple rules.</p>
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <div data-testid="guidelines-do" className="bg-white border border-black/10 rounded-2xl p-6">
            <h3 className="font-semibold text-lg text-[#2E7D32] mb-4">Please do</h3>
            <ul className="space-y-3">
              {DO.map((d) => (
                <li key={d} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#E3F0E3] flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-[#2E7D32]" /></span>
                  {d}
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="guidelines-dont" className="bg-white border border-black/10 rounded-2xl p-6">
            <h3 className="font-semibold text-lg text-red-600 mb-4">Please don't</h3>
            <ul className="space-y-3">
              {DONT.map((d) => (
                <li key={d} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-red-50 flex items-center justify-center shrink-0"><X className="w-3.5 h-3.5 text-red-600" /></span>
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-6">Breaches may result in suspension or permanent account closure.</p>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="bg-[#FFF0E8] border border-[#FF5A00]/20 rounded-3xl p-10 sm:p-14 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Ready to get started?</h2>
          <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Browse gear near you, or list something of your own and start earning.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/browse" data-testid="about-cta-browse" className="flex items-center gap-1.5 bg-[#0A0A0A] text-white rounded-full px-6 py-3 font-medium hover:bg-[#FF5A00] transition-colors active:scale-95">
              Browse items <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/how-it-works" data-testid="about-cta-how" className="rounded-full px-6 py-3 font-medium border border-black/15 hover:border-black transition-colors">
              How it works
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
